/**
 * The user's language (AgentSettings.language): an ISO 639-1 code, French by default. The agent writes in it; the fixed
 * texts sent without the model (commands, approvals, reminders, login codes) are French for "fr" and English for any
 * other language. The app's own copy stays French.
 */

export const DEFAULT_LANGUAGE = "fr";

/** The languages offered in Réglages (any other ISO 639-1 code the agent stored is shown as well). */
export const LANGUAGE_OPTIONS: readonly { code: string; label: string }[] = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
];

const ISO_639_1 = /^[a-z]{2}$/;

function displayNames(locale: string): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames([locale], { type: "language", fallback: "none" });
  } catch {
    return null;
  }
}

/** Two lowercase letters naming a language (checked against the runtime's language names when it has them). */
export function isLanguageCode(value: string): boolean {
  if (!ISO_639_1.test(value)) return false;
  const names = displayNames("en");
  if (!names) return true;
  try {
    const name = names.of(value);
    return name !== undefined && name !== value;
  } catch {
    return false;
  }
}

/** "en" → "anglais" (French name, for the French prompts); the code itself when the runtime does not know it. */
export function languageNameFr(code: string): string {
  try {
    return displayNames("fr")?.of(code) ?? code;
  } catch {
    return code;
  }
}

/** The fixed texts of this language are the English ones (every language but French). */
export function usesEnglishCopy(language: string | null | undefined): boolean {
  return (language ?? DEFAULT_LANGUAGE) !== "fr";
}
